import React, { useEffect } from "react";
import { api } from "@/lib/api";
import { colonyApi } from "@/lib/colonyApi";
import { registerOfflineClient, initOfflineSync } from "@/lib/offlineQueue";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ManifestSwapper from "@/components/ManifestSwapper";
import { ManagementUsageTracker } from "@/components/ManagementUsageTracker";
import ManagementSignup from "@/pages/managementusa/ManagementSignup";
import ManagementVerifySignup from "@/pages/managementusa/ManagementVerifySignup";
import ManagementInvitation from "@/pages/managementusa/ManagementInvitation";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ColonyAuthProvider, useColonyAuth } from "@/contexts/ColonyAuthContext";
import LoginPage from "@/pages/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Customers from "@/pages/admin/Customers";
import CustomerDetail from "@/pages/admin/CustomerDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import QuoteAccept from "@/pages/QuoteAccept";
import AuthCallback from "@/pages/AuthCallback";
import OrderPay from "@/pages/OrderPay";
import RdSetup from "@/pages/RdSetup";
import Tickets from "@/pages/admin/Tickets";
import Orders from "@/pages/admin/Orders";
import Products from "@/pages/admin/Products";
import DriverDay from "@/pages/admin/DriverDay";
import TrackDriver from "@/pages/admin/TrackDriver";
import Invoices from "@/pages/admin/Invoices";
import InvoiceDetail from "@/pages/admin/InvoiceDetail";
import Settings from "@/pages/admin/Settings";
import Users from "@/pages/admin/Users";
import Finance from "@/pages/admin/Finance";
import ManagementUsaNJ from "@/pages/admin/ManagementUsaNJ";
import DriverExpenses from "@/pages/driver/DriverExpenses";
import WorkerLayout from "@/pages/worker/WorkerLayout";
import WorkerDashboard from "@/pages/worker/WorkerDashboard";
import WorkerForgotPassword from "@/pages/worker/WorkerForgotPassword";
import WorkerResetPassword from "@/pages/worker/WorkerResetPassword";
import MgmtLayout from "@/pages/managementusa/MgmtLayout";
import MgmtSettings from "@/pages/managementusa/MgmtSettings";
import MgmtForgotPassword from "@/pages/managementusa/MgmtForgotPassword";
import MgmtResetPassword from "@/pages/managementusa/MgmtResetPassword";
import SharedMgmtLogin from "@/pages/managementusa/SharedMgmtLogin";
import PublicVideoLibrary from "@/pages/managementusa/PublicVideoLibrary";
import PublicVideoWatch from "@/pages/managementusa/PublicVideoWatch";
import InvoiceFileLink from "@/pages/managementusa/InvoiceFileLink";
import DesktopPortalPage from "@/pages/desktop/DesktopPortalPage";
import DesktopActivatePage from "@/pages/desktop/DesktopActivatePage";
import DesktopGoogleCallback from "@/pages/desktop/DesktopGoogleCallback";
import CustomerLayout from "@/pages/customer/CustomerLayout";
import CustomerPortal from "@/pages/customer/CustomerPortal";
import CustomerInvoiceDetail from "@/pages/customer/CustomerInvoiceDetail";
import CustomerShop from "@/pages/customer/CustomerShop";
import ColonyLogin from "@/pages/colony/ColonyLogin";
import SignDocumentPage from "@/pages/SignDocumentPage";
import ColonyAcceptInvite from "@/pages/colony/ColonyAcceptInvite";
import ColonyForgotPassword from "@/pages/colony/ColonyForgotPassword";
import ColonyAuthCallback from "@/pages/colony/ColonyAuthCallback";
import ColonyLayout from "@/pages/colony/ColonyLayout";
import ColonyTickets from "@/pages/colony/ColonyTickets";
import ColonyStatement from "@/pages/colony/ColonyStatement";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SecurityPolicy from "@/pages/SecurityPolicy";
import { managementLoginPath } from "@/lib/managementDeepLink";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm overline">Loading…</div>
    </div>
  );
}

function Protected({ children, roles, loginPath = "/login" }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) {
    const target = loginPath === "/managementusa"
      ? managementLoginPath(`${loc.pathname}${loc.search}`)
      : loginPath;
    return <Navigate to={target} replace />;
  }
  // Dual-role support: a driver (or other pool staff) with the
  // `is_worker` flag can enter worker-gated routes too.
  const allowed =
    !roles ||
    roles.includes(user.role) ||
    (roles.includes("worker") && user.is_worker === true) ||
    (roles.includes("mgmt") && user.is_mgmt === true);
  if (!allowed) {
    return <Navigate to={homeForRole(user)} replace />;
  }
  return children;
}

function homeForRole(user) {
  if (user.role === "admin" || user.role === "secretary") return "/admin";
  if (user.role === "mgmt") return "/managementusa/admin";
  if (user.role === "driver") return "/driver";
  if (user.role === "worker") return "/worker";
  return "/portal";
}

// Colony portal uses a completely separate auth context. This wrapper
// checks that context — never the pool one — so a pool session in the
// same browser can't grant access here.
function ColonyProtected({ children }) {
  const { user, loading } = useColonyAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  // Keep the intended destination (e.g. /colonies/statement from an email
  // link) so the sign-in page can land the contact right back on it.
  if (!user) return <Navigate to={managementLoginPath(`${loc.pathname}${loc.search}`)} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user)} replace />;
}

function App() {
  useEffect(() => {
    registerOfflineClient("api", api);
    registerOfflineClient("colony", colonyApi);
    return initOfflineSync();
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <ColonyAuthProvider>
          <BrowserRouter>
          <ManifestSwapper />
          <ManagementUsageTracker />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign/:token" element={<SignDocumentPage />} />
            <Route path="/desktop" element={<DesktopPortalPage />} />
            <Route path="/desktop/activate" element={<DesktopActivatePage />} />
            <Route path="/desktop/auth-callback" element={<DesktopGoogleCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/quotes/accept/:token" element={<QuoteAccept />} />
            <Route path="/orders/pay/:token" element={<OrderPay />} />
            <Route path="/rd-setup/:token" element={<RdSetup />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/security-policy" element={<SecurityPolicy />} />
            <Route path="/security" element={<SecurityPolicy />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/admin" element={<Protected roles={["admin", "secretary"]}><AdminLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="products" element={<Products />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="orders" element={<Orders />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              <Route path="track" element={<TrackDriver />} />
              <Route path="users" element={<Protected roles={["admin"]}><Users /></Protected>} />
              <Route path="finance" element={<Protected roles={["admin"]}><Finance /></Protected>} />
              <Route path="managementusa" element={<Navigate to="/managementusa" replace />} />
              {/* Legacy slug — redirect to the new URL so old bookmarks still work. */}
              <Route path="mgmt-usa-nj" element={<Navigate to="/managementusa" replace />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/driver" element={<Protected roles={["admin", "secretary", "driver"]}><DriverDay /></Protected>} />
            <Route path="/driver/expenses" element={<Protected roles={["admin", "driver"]}><DriverExpenses /></Protected>} />
            {/* Worker portal — fully branded Management USA NJ, no Catskills entry
                points. Auth still lives in the pool `users` collection so the
                worker's watcher membership continues to drive ticket visibility. */}
            <Route path="/worker/login" element={<Navigate to="/managementusa" replace />} />
            <Route path="/worker/forgot-password" element={<WorkerForgotPassword />} />
            <Route path="/worker/reset-password" element={<WorkerResetPassword />} />
            <Route
              path="/worker"
              element={
                <Protected roles={["worker", "admin"]} loginPath="/managementusa">
                  <WorkerLayout />
                </Protected>
              }
            >
              <Route index element={<WorkerDashboard />} />
            </Route>
            {/* Management USA NJ unified portal.
                /managementusa is the SINGLE branded entry point — admins,
                workers, and colony contacts all sign in there. The login
                page routes each identity to their correct dashboard:
                admin/secretary → /managementusa/admin, worker → /worker,
                colony → /colonies. */}
            <Route path="/managementusa" element={<SharedMgmtLogin />} />
            <Route path="/managementusa/Videos" element={<PublicVideoLibrary />} />
            <Route path="/managementusa/Videos/:audience/:section" element={<PublicVideoWatch />} />
            <Route path="/managementusa/InvoiceFiles/:invoiceId/:attachmentId" element={<InvoiceFileLink />} />
            <Route path="/managementusa/signup" element={<ManagementSignup />} />
            <Route path="/managementusa/verify-signup" element={<ManagementVerifySignup />} />
            <Route path="/managementusa/invitation" element={<ManagementInvitation />} />
            <Route path="/managementusa/login" element={<Navigate to="/managementusa" replace />} />
            <Route path="/managementusa/forgot-password" element={<MgmtForgotPassword />} />
            <Route path="/managementusa/reset-password" element={<MgmtResetPassword />} />
            <Route
              path="/managementusa/admin"
              element={
                <Protected roles={["admin", "secretary", "mgmt"]} loginPath="/managementusa">
                  <MgmtLayout />
                </Protected>
              }
            >
              <Route index element={<ManagementUsaNJ />} />
              <Route path="settings" element={<MgmtSettings />} />
              <Route path="settings/access" element={<MgmtSettings />} />
              <Route path="settings/tutorials" element={<MgmtSettings />} />
              <Route path="connections" element={<Navigate to="/managementusa/admin?invitations=1" replace />} />
            </Route>
            <Route path="/portal" element={<Protected roles={["customer"]}><CustomerLayout /></Protected>}>
              <Route index element={<CustomerPortal />} />
              <Route path="shop" element={<CustomerShop />} />
              <Route path="invoices/:id" element={<CustomerInvoiceDetail />} />
            </Route>
            {/* Bungalow Colony portal — separate branded login + dashboard.
                Uses its own auth context; ColonyProtected reads only the
                colony session, never the pool session.
                Colony sign-in has moved to the shared /managementusa entry
                point — /colonies/login redirects there for legacy links. */}
            <Route path="/colonies/login" element={<Navigate to="/managementusa" replace />} />
            <Route path="/colonies/forgot-password" element={<ColonyForgotPassword />} />
            <Route path="/colonies/accept-invite" element={<ColonyAcceptInvite />} />
            <Route path="/colonies/auth-callback" element={<ColonyAuthCallback />} />
            <Route path="/colonies" element={<ColonyProtected><ColonyLayout /></ColonyProtected>}>
              {/* Statements are temporarily disabled for colony users —
                  the index redirects straight to service requests.
                  Re-enable by restoring <ColonyDashboard /> here. */}
              <Route index element={<Navigate to="/colonies/tickets" replace />} />
              <Route path="tickets" element={<ColonyTickets />} />
              <Route path="statement" element={<ColonyStatement />} />
              <Route path="managers" element={<Navigate to="/colonies?invitations=1" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ColonyAuthProvider>
      </AuthProvider>
      <Toaster richColors position="bottom-right" offset={64} />
    </div>
  );
}

export default App;
